import type { ReactNode } from 'react';
import { useTranslate } from '@refinedev/core';
import { Descriptions, Divider, Space, Tag, Typography } from 'antd';
import type { DescriptionsProps } from 'antd';
import dayjs from 'dayjs';
import type { DateRange, PublicationDetailOut } from '@/api/generated/schemas';
import { orDash, useVerticalDescProps } from '@/components/common/show/shared';
import { formatDate } from '@/utils/format';
import {
  exclusivityState,
  rangeToPickerValue,
} from '@/pages/user_catalog/publications/show/exclusivityWindow';
import { exclusiveUntilToPickerValue } from '@/pages/user_catalog/publications/show/formatExclusiveUntil';
import { dateRangeState } from '@/pages/user_catalog/publications/show/publicationTimeline';
import type { RangeState } from '@/pages/user_catalog/publications/show/publicationTimeline';
import { availabilityStatusColors } from '../../publicationColors';

const { Text } = Typography;

const rangeStateColors: Record<RangeState, string | undefined> = {
  active: 'green',
  ended: undefined,
  planned: 'blue',
};

interface DrawerAvailabilityTabProps {
  record: PublicationDetailOut;
}

export const DrawerAvailabilityTab = ({
  record,
}: DrawerAvailabilityTabProps) => {
  const t = useTranslate();
  const verticalDescProps = useVerticalDescProps();
  const now = dayjs();
  const T = 'user_catalog.publications.show';

  const rangeLine = (
    from: string | null | undefined,
    to: string | null | undefined,
    state: RangeState
  ): ReactNode => (
    <Space size={8}>
      <span>
        {orDash(from)} –{' '}
        {to ?? <Text type="secondary">{t(`${T}.windows.open_ended`)}</Text>}
      </span>
      <Tag color={rangeStateColors[state]}>
        {t(`${T}.windows.state_${state}`)}
      </Tag>
    </Space>
  );

  const windows = (record.available_ranges ?? []).map(
    (range: DateRange, index) => (
      <div key={index}>
        {rangeLine(
          formatDate(range.lower),
          range.upper ? formatDate(range.upper) : null,
          dateRangeState(range, now)
        )}
      </div>
    )
  );

  // Displayed as inclusive days, matching the show page and its modal.
  const exclusiveDays = rangeToPickerValue(record.exclusive_ranges);
  const exclusiveState = exclusivityState(record.exclusive_ranges, now);

  const statusItems: DescriptionsProps['items'] = [
    {
      key: 'availability_status',
      label: t('user_catalog.publications.fields.availability_status'),
      children: record.availability_status ? (
        <Tag
          color={
            availabilityStatusColors[record.availability_status] ?? 'default'
          }
        >
          {t(
            `user_catalog.publications.availability_status.${record.availability_status}`
          )}
        </Tag>
      ) : (
        orDash(null)
      ),
    },
    {
      key: 'switch_mode',
      label: t('user_catalog.publications.fields.switch_mode'),
      children: record.switch_mode
        ? t(`user_catalog.publications.switch_mode.${record.switch_mode}`)
        : orDash(null),
    },
    {
      key: 'exclusivity',
      label: t(`${T}.sections.exclusivity`),
      span: 2,
      children:
        exclusiveDays && exclusiveState
          ? rangeLine(
              exclusiveDays[0]?.format('DD.MM.YYYY'),
              exclusiveDays[1]?.format('DD.MM.YYYY'),
              exclusiveState
            )
          : orDash(null),
    },
    {
      key: 'windows',
      label: t(`${T}.sections.publication_windows`),
      span: 2,
      children: windows.length ? (
        <Space direction="vertical" size={4}>
          {windows}
        </Space>
      ) : (
        orDash(null)
      ),
    },
  ];

  const formats = (
    [
      ['ebook', record.ebook],
      ['audio', record.audio],
    ] as const
  ).filter(([, data]) => data != null);

  return (
    <>
      <Descriptions {...verticalDescProps} column={2} items={statusItems} />
      {formats.map(([kind, data]) => (
        <div key={kind}>
          <Divider orientation="left" orientationMargin={0}>
            {t(`user_catalog.publications.tabs.${kind}`)}
          </Divider>
          <Descriptions
            {...verticalDescProps}
            column={3}
            items={[
              {
                key: 'published_at',
                label: t(`${T}.windows.published_at`),
                children: orDash(formatDate(data?.published_at)),
              },
              {
                key: 'first_available_from',
                label: t(`${T}.windows.first_available`),
                children: orDash(formatDate(data?.first_available_from)),
              },
              {
                key: 'exclusive_until',
                label: t(`${T}.windows.exclusive_until`),
                // The last exclusive day — the stored value is the first day after it.
                children: orDash(
                  exclusiveUntilToPickerValue(data?.exclusive_until)?.format(
                    'DD.MM.YYYY'
                  )
                ),
              },
              {
                key: 'available_from',
                label: t(`${T}.windows.available_from`),
                children: orDash(formatDate(data?.available_from)),
              },
              {
                key: 'available_till',
                label: t(`${T}.windows.available_till`),
                children: orDash(formatDate(data?.available_till)),
              },
              {
                key: 'withdrawal_at',
                label: t(`${T}.windows.withdrawal`),
                children: orDash(formatDate(data?.withdrawal_at)),
              },
            ]}
          />
        </div>
      ))}
    </>
  );
};
